// External dependencies
import { ofType } from 'redux-observable';
import { Observable, switchMap } from 'rxjs';

// Local dependencies
import { getClient } from '../../../../../clients/demirbank';
import compressedImage from '../../../../common/compressedImage';
import { cleanPayload } from '../../../../common/helpers';
import { Logo } from '../../../utils/types';
import { updateCategoryError, updateCategorySuccess } from './actions';
import { updateCategoryMutation } from './mutation';
import { UpdateCategory, UpdateCategoryAction, UpdateCategoryActionTypes, UpdateCategoryLogo } from './types';

function isLogo(logo: UpdateCategoryLogo): logo is Logo {
  return 'url' in logo;
}

export function updateCategoryEpic(action$: Observable<UpdateCategoryAction>) {
  return action$.pipe(
    ofType(UpdateCategoryActionTypes.UPDATE_CATEGORY),
    switchMap((action: UpdateCategory) =>
      updateCategory(action)
        .then(updateCategorySuccess)
        .catch((error) => updateCategoryError(error)),
    ),
  );
}

async function processLogo(logo: UpdateCategoryLogo): Promise<Logo> {
  const result: Logo = { url: '' };

  if (isLogo(logo) && logo?.url && logo?.url?.startsWith('https')) {
    result.url = undefined;
  } else {
    const compressedLogo = await compressedImage(logo[0]);

    result.url = compressedLogo.replace(/^data:image\/[a-z]+;base64,/, '');
  }

  return result;
}

async function updateCategory(action: UpdateCategory) {
  const graphQLClient = await getClient();

  const categoryData = { ...action.input };

  if (categoryData?.tags?.length > 0 && Array.isArray(categoryData.tags)) {
    const tags = new Set(categoryData.tags.map((item) => item.id ?? item.value));

    categoryData.tags = Array.from(tags);
  } else {
    categoryData.tags = [];
  }

  if (categoryData.logo) {
    const processedLogo = await processLogo(categoryData.logo);

    if (!processedLogo.url) {
      delete categoryData?.logo;
    } else {
      categoryData.logo = processedLogo;
    }
  } else {
    delete categoryData?.logo;
  }

  const input = cleanPayload(categoryData);

  const {
    data: { updateCategory },
  } = await graphQLClient.query({
    query: updateCategoryMutation,
    variables: { input },
  });

  return updateCategory;
}
